// src/pages/Resources.js
import React from 'react';
import { Helmet } from 'react-helmet';

const Page = () => {
  return (
    <>
      <Helmet>
        <title>Resource Page | Project V.O.T.E.</title>
      </Helmet>
      <div className="mx-auto max-w-7xl sm:px-4 md:px-6 lg:px-8 h-full">
        {/* Header Section */}
        <div className="relative rounded-t-lg bg-stone-100 py-8 sm:py-16 lg:py-24">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center">
              <div className="lg:flex-1">
                <h1 className="mt-1 text-4xl font-extrabold sm:text-5xl lg:text-6xl">
                  Resources
                </h1>
                <p className="max-w-xl mt-5 text-xl text-gray-900">
                  Explore important resources and guidelines related to voter registration and Texas voting history.
                </p>
              </div>
              <div className="mt-8 lg:mt-0 lg:ml-8 lg:flex-shrink-0">
                <img
                  src={`${process.env.PUBLIC_URL}/images/the-alamo.webp`}
                  alt="An illustration of The Alamo"
                  className="w-[400px] h-[300px]"
                />
              </div>
            </div>
          </div>
        </div>


        {/* Content Section */}
        <div>
          <div className="bg-white p-6 shadow-lg">
            <dl className="space-y-10">
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">
                  <h2>High School Principals as Deputy Voter Registrars</h2>
                </dt>
                <dd className="mt-2 text-base text-gray-500">
                  In Texas, high school principals play a crucial role in fostering civic engagement among young adults. According to the Texas Election Code (<a href="https://statutes.capitol.texas.gov/Docs/EL/htm/EL.13.htm" className="text-sky-900 underline hover:text-sky-700">Sec. 13.046</a>), every principal of a public or private high school, or their designee, is required to serve as a High School Deputy Registrar. In this capacity, principals are responsible for distributing voter registration forms and collecting completed applications from eligible students and employees.
                </dd>
                <dd className="mt-2 text-base text-gray-500">
                  Each academic year, principals must ensure that voter registration forms are provided to students who are or will be 18 years of age or older. The Texas Administrative Code (<a href="https://texreg.sos.state.tx.us/public/readtac$ext.TacPage?sl=R&app=9&p_dir=&p_rloc=&p_tloc=&p_ploc=&pg=1&p_tac=&ti=1&pt=4&ch=81&rl=7" className="text-sky-900 underline hover:text-sky-700">Title 1, Part 4, Chapter 81, Rule §81.7</a>) further outlines the procedures principals must follow to comply with this responsibility, including the timely submission of completed registration forms to the county voter registrar.
                </dd>
                <dd className="mt-2 text-base text-gray-500">
                  By deputizing high school principals, Texas aims to encourage voter registration and participation among its youngest eligible voters, ensuring that students are prepared to engage in the democratic process as they reach voting age.
                </dd>
              </div>

              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">
                  <h2>Explore Texas Voting History:<br />Essential Resources for Educators</h2>
                </dt>
                <dd className="mt-2 text-base text-gray-500">
                  Welcome to our curated collection of resources designed to help educators bring the rich history of voting in Texas into the classroom. The resources provided by these Texas government agencies and organizations include digital tools, historical archives, and educational materials on voting. Whether you're looking for primary sources, historical context, or ready-to-use lesson plans, these resources can support your efforts to engage students with the fascinating and evolving story of civic participation in Texas.
                </dd>
                <dd className="mt-4">
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong><a href="https://www.sos.state.tx.us/elections/" className="text-sky-900 underline hover:text-sky-700">Texas Secretary of State - Elections Division:</a></strong> This agency offers a comprehensive range of digital resources, including historical voter turnout data, election results, and educational materials on the voting process in Texas. Teachers can access these resources to teach about the history and significance of voting in the state.
                    </li>
                    <li>
                      <strong><a href="https://www.tsl.texas.gov/" className="text-sky-900 underline hover:text-sky-700">Texas State Library and Archives Commission (TSLAC):</a></strong> TSLAC provides access to a wealth of historical documents and archives related to elections and voting in Texas. Their digital collections include primary sources such as voter registration records, historical election returns, and documents on the evolution of voting rights in Texas.
                    </li>
                    <li>
                      <strong><a href="https://thc.texas.gov/" className="text-sky-900 underline hover:text-sky-700">Texas Historical Commission:</a></strong> This commission manages historical markers and sites throughout the state, many of which relate to significant voting events or locations. They also offer online resources and lesson plans that incorporate these historical sites into the teaching of Texas history, including the history of voting.
                    </li>
                    <li>
                      <strong><a href="https://www.tshaonline.org/home" className="text-sky-900 underline hover:text-sky-700">Texas State Historical Association (TSHA):</a></strong> While not a government agency, TSHA works closely with state institutions and offers the "Handbook of Texas," an extensive digital resource that includes articles on the history of voting and elections in Texas. This can be a valuable tool for teachers looking for detailed historical context.
                    </li>
                    <li>
                      <strong><a href="https://www.thestoryoftexas.com/" className="text-sky-900 underline hover:text-sky-700">The Bullock Texas State History Museum:</a></strong> The Bullock Museum provides digital exhibits and resources on various aspects of Texas history, including the history of voting. These resources are designed to be educational and can be integrated into classroom lessons.
                    </li>
                  </ul>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </>
  );
}

export default Page;
